import { ITradeAllocation } from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import { ITicketAllocation } from "@/store/tradeFromScratchStore";

// LQC-2941 / LQC-2949 - splitting a trade across portfolios. The ticket's own
// portfolioCode is the first allocation and takes whatever the splits leave, so the
// allocations always sum to the trade nominal (spec F5) and there is no total to retype.

export interface ITicketIssue {
  key: string;
  label: string;
}

export const ALLOCATIONS_KEY = "additionalAllocations";

export function returnSplitNominalTotal(splits: ITicketAllocation[]): number {
  return splits.reduce((sum, split) => sum + (split.nominal ?? 0), 0);
}

// The first portfolio's share, or null until the trade nominal is known.
export function returnPrimaryAllocationNominal(
  total: number | null,
  splits: ITicketAllocation[],
): number | null {
  if (typeof total !== "number") return null;
  return total - returnSplitNominalTotal(splits);
}

// Caps a split at what the other splits leave of the trade nominal. Null when nothing is
// left, including before the nominal is entered.
export function returnCappedSplitNominal(
  total: number | null,
  splits: ITicketAllocation[],
  index: number,
  nominal: number | null,
): number | null {
  if (nominal === null || typeof total !== "number") return null;
  const others = returnSplitNominalTotal(splits.filter((_, i) => i !== index));
  const available = total - others;
  if (available <= 0) return null;
  return Math.min(nominal, available);
}

// Numbered from 2: the ticket's own portfolio is allocation 1.
export function returnAllocationIssues(
  total: number | null,
  splits: ITicketAllocation[],
): ITicketIssue[] {
  const issues: ITicketIssue[] = [];
  splits.forEach((split, index) => {
    const label = `Allocation ${index + 2}`;
    if (!split.portfolioCode) {
      issues.push({ key: `${ALLOCATIONS_KEY}.${index}.portfolioCode`, label: `${label} Portfolio` });
    }
    if (!(typeof split.nominal === "number" && split.nominal > 0)) {
      issues.push({ key: `${ALLOCATIONS_KEY}.${index}.nominal`, label: `${label} Nominal` });
    }
  });
  const primary = returnPrimaryAllocationNominal(total, splits);
  if (splits.length === 0 || !(typeof total === "number" && total > 0) || primary === null) {
    return issues;
  }
  // Zero is the splits taking the whole block, not exceeding it: the first portfolio needs a share.
  if (primary === 0) {
    issues.push({ key: ALLOCATIONS_KEY, label: "Portfolio Nominal (the splits use it all)" });
  } else if (primary < 0) {
    issues.push({ key: ALLOCATIONS_KEY, label: "Allocations exceed the Nominal" });
  }
  return issues;
}

// The allocations[] every trade message sends. returnNominal2 (swaps) maps each
// allocation's nominal to its Leg 2 nominal.
export function returnWireAllocations(
  portfolioCode: string,
  total: number,
  splits: ITicketAllocation[],
  returnNominal2?: (nominal: number) => number,
): ITradeAllocation[] {
  const allocations = [
    { portfolio: portfolioCode, nominal: returnPrimaryAllocationNominal(total, splits) as number },
    ...splits.map((split) => ({
      portfolio: split.portfolioCode,
      nominal: split.nominal as number,
    })),
  ];
  if (!returnNominal2) return allocations;
  return allocations.map((allocation) => ({
    ...allocation,
    nominal2: returnNominal2(allocation.nominal),
  }));
}
